// DS6.20
import React from 'react';
import isEqual from 'lodash/isEqual';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import { StandardMenuProps } from '../../MenuPreset/StandardMenu';
import { NullableDropdownItem } from '../../types';
import StandardDropdownMenu from '../StandardDropdown/StandardDropdownMenu';
import {
  PopperDropdownManagerProps,
  PopperDropdownManager,
} from '../../PopperDropdownLayout';
import PopperGhostToggleButton, {
  PopperGhostToggleButtonProps,
} from '../../toggles/PopperGhostToggleButton';

export interface Props
  extends Omit<PopperGhostToggleButtonProps, 'aria-haspopup'> {
  items: NullableDropdownItem[];

  popperManagerProps?: PopperDropdownManagerProps;
  standardMenuProps?: Omit<
    StandardMenuProps,
    'items' | 'role' | 'id' | 'menuScrollable'
  >;
}

/**
 * Our DS `GhostButton` with a context menu provided by our dropdown library.
 *
 * You can customize the popper or standarded menu instance via `popperManagerProps` or `standardMenuProps` respectively.
 */
const GhostButtonWithContextMenu: React.FC<Props> = React.memo(
  ({ items, popperManagerProps, standardMenuProps, ...rest }) => {
    const { filteredItems } = useDropdownItems(items);

    const toggleId = useUniqueId();
    const dropdownId = useUniqueId();

    return (
      <PopperDropdownManager {...popperManagerProps}>
        <PopperGhostToggleButton
          id={toggleId}
          aria-haspopup="menu"
          aria-controls={dropdownId}
          {...rest}
        />
        <StandardDropdownMenu
          id={dropdownId}
          role="menu"
          aria-labelledby={toggleId}
          menuScrollable
          items={filteredItems}
          {...standardMenuProps}
        />
      </PopperDropdownManager>
    );
  },
  // Deep equal from lodash so we can compare the standard menu and popper manager prop collections
  isEqual
);
GhostButtonWithContextMenu.displayName = 'BrandedButtonWithContextMenu';

export default GhostButtonWithContextMenu;
